import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styled from "styled-components";
import tw from "twin.macro";
import { css } from "styled-components/macro"; //eslint-disable-line
import {ReactComponent as SvgDotPatternIcon} from "../images/dot-pattern.svg"

const Container = tw.div`relative`;
const Content = tw.div`max-w-screen-xl mx-auto py-20 lg:py-24`;

const FormContainer = styled.div`
  ${tw`p-10 sm:p-12 md:p-16 bg-primary-500 text-gray-100 rounded-lg relative`}
  form {
    ${tw`mt-4`}
  }
  h2 {
    ${tw`text-3xl sm:text-4xl font-bold`}
  }
  #name-input, #email-input, #message-input {
    border: none !important;
    border-bottom: 2px solid #f3f4f6 !important;
    border-radius: 0 !important;
    ${tw`w-full bg-transparent text-gray-100 text-base font-medium tracking-wide py-2 text-gray-100 transition duration-200`};

    &:focus {
      border-bottom: 2px solid #ca8a04 !important; /* yellow-600 */
      outline: none !important;
      border-radius: 0 !important;
    }

    ::placeholder {
      ${tw`text-gray-500`}
    }
  }
`;

const TwoColumn = tw.div`flex flex-col sm:flex-row justify-between`;
const Column = tw.div`sm:w-5/12 flex flex-col`;
const InputContainer = tw.div`relative py-5 mt-6`;
const Label = tw.label`absolute top-0 left-0 tracking-wide font-semibold text-sm`;
const Input = tw.input`rounded-none`;
const CheckboxDiv = tw.div`flex items-center gap-2 mt-4`; 
const CheckBoxLabel = tw.label`flex items-center cursor-pointer relative`;
const CheckBoxSpan = tw.span`absolute text-white opacity-0 peer-checked:opacity-100 transform -translate-x-1/2 -translate-y-1/2`;
const CheckBoxInput = tw.input`
  h-5 w-5 cursor-pointer transition-all appearance-none rounded shadow-sm
  border border-s-teal-200 checked:bg-yellow-600 checked:border-yellow-600
  !border-solid !border-2 !border-gray-300
  bg-white
`;
const CheckBoxSvg = tw.svg`h-5 w-5`;
const CheckBoxText = tw.span`cursor-pointer ml-2 font-semibold text-sm`;
const TextArea = tw.textarea`h-24 sm:h-full resize-none`;
const SubmitButton = tw.button`
  w-full sm:w-32 mt-6 py-3
  !border-none !bg-gray-100 !text-primary-500
  rounded-full !font-bold tracking-wide shadow-lg uppercase text-sm
  transition duration-300 transform focus:outline-none focus:shadow-outline
  hover:!bg-yellow-600 hover:!text-gray-200 hocus:-translate-y-px hocus:shadow-xl
  disabled:!bg-gray-400 disabled:!text-gray-200 disabled:!cursor-not-allowed
`;
const AlertDiv = tw.div`mt-4 bg-red-200 border border-red-400 text-sm text-red-800 rounded-lg p-4`;
const PopOverDiv = tw(motion.div)`fixed bottom-0 pb-4 right-0 transform -translate-x-1/2 z-50`;
const SentDiv = tw.div`bg-teal-500 text-white px-6 py-3 rounded shadow-lg`;


const SvgDotPattern1 = tw(SvgDotPatternIcon)`absolute bottom-0 right-0 transform translate-y-1/2 translate-x-1/2 -z-10 opacity-50 text-primary-500 fill-current w-24`

export default () => {
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [agree, setAgree] = useState(false);
  const [loading, setLoading] = useState(false);
  const nameRef = useRef();
  const emailRef = useRef();
  const messageRef = useRef();
  const formRef = useRef();

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const name = nameRef.current.value.trim();
    const email = emailRef.current.value.trim();
    const message = messageRef.current.value.trim();

    if (!name) {
      setError("お名前を入力してください。");
      setLoading(false);
      return;
    }
    if (!email) {
      setError("メールアドレスを入力してください。");
      setLoading(false);
      return;
    }
    if (!validateEmail(email)) {
      setError("メールアドレスの形式が不正です。");
      setLoading(false);
      return;
    }
    if (!message) {
      setError("お問い合わせの本文を入力してください。");
      setLoading(false);
      return;
    }
    setError("");

    // GoogleフォームへfetchでPOST（画面遷移なし）
    const formData = new FormData();
    formData.append("entry.1560568602", name);
    formData.append("emailAddress", email);
    formData.append("entry.1134485471", message);

    try {
      await fetch("https://docs.google.com/forms/u/2/d/e/1FAIpQLSfynEXGqG7YUmt0yUxDMfUSS8261Z8pyh1APFDq-neZATD2fg/formResponse", {
        method: "POST",
        mode: "no-cors",
        body: formData,
      });
      setSuccess(true);
      nameRef.current.value = "";
      emailRef.current.value = "";
      messageRef.current.value = "";
      setAgree(false);
    } catch (err) {
      setError("送信に失敗しました。時間をおいて再度お試しください。");
    }
    setLoading(false);
  };

  // 成功Popoverを3秒後に自動非表示
  React.useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  return (
    <Container id="contact">
      <Content>
        <FormContainer>
          <AnimatePresence>
            {success && (
              <PopOverDiv
                initial={{ x: "100vw", opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: "100vw", opacity: 0 }}
                transition={{ type: "spring", stiffness: 400, damping: 30 }}
              >
                <SentDiv className="bg-green-500 text-white px-6 py-3 rounded shadow-lg">
                  送信が完了しました。
                </SentDiv>
              </PopOverDiv>
            )}
          </AnimatePresence>
          <div tw="mx-auto max-w-4xl">
            <h2>お問い合わせ</h2>
            <form
              ref={formRef}
              action="https://docs.google.com/forms/u/2/d/e/1FAIpQLSfynEXGqG7YUmt0yUxDMfUSS8261Z8pyh1APFDq-neZATD2fg/formResponse"
              method='post'
              onSubmit={handleSubmit}
            >
              <TwoColumn>
                <Column>
                  <InputContainer>
                    <Label htmlFor="name-input">お名前</Label>
                    <Input id="name-input" type="text" name="entry.1560568602" placeholder="お名前を入力してください" ref={nameRef} />
                  </InputContainer>
                  <InputContainer>
                    <Label htmlFor="email-input">メールアドレス</Label>
                    <Input id="email-input" type="email" name="emailAddress" placeholder="メールアドレスを入力してください" ref={emailRef} />
                  </InputContainer>
                </Column>
                <Column>
                  <InputContainer tw="flex-1">
                    <Label htmlFor="message-input">本文</Label>
                    <TextArea id="message-input" name="entry.1134485471" placeholder="お問い合わせの本文を入力してください" ref={messageRef}/>
                  </InputContainer>
                </Column>
              </TwoColumn>
              <CheckboxDiv>
                <CheckBoxLabel htmlFor="agree-input">
                  <CheckBoxInput
                    className="peer"
                    type="checkbox"
                    id="agree-input"
                    checked={agree}
                    onChange={e => setAgree(e.target.checked)}
                  />
                  <CheckBoxSpan>
                    <CheckBoxSvg
                      fill="none"
                      width="18px"
                      height="18px"
                      strokeWidth="2"
                      color="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path d="M5 13L9 17L19 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path>
                    </CheckBoxSvg>
                  </CheckBoxSpan>
                </CheckBoxLabel>
                <CheckBoxText>
                  <a
                    href="/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ textDecoration: "underline" }}
                  >
                    個人情報保護方針
                  </a>
                  の内容に同意する
                </CheckBoxText>
              </CheckboxDiv>
              {error && (
                <AlertDiv role="alert">
                  <span>{error}</span>
                </AlertDiv>
              )}
              <SubmitButton
                type="submit"
                value="Submit"
                disabled={!agree || loading}
                className={!agree || loading ? "opacity-50 cursor-not-allowed" : ""}
              >
                送信
              </SubmitButton>
            </form>
          </div>
          <SvgDotPattern1 />
        </FormContainer>
      </Content>
    </Container>
  );
};
